import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { CalloutBlock, StyledP, PrimaryLoadingButton } from 'components';
import styled from 'styled-components';
import { Show } from 'components/Functional';
import { GlobalState, isFetching, LoadingState } from 'store/constants';
import { numberToUSD } from 'helpers/currency';
import { storeCreditSelector } from 'store/customer/selectors';
import { applyStoreCreditRequest, removeStoreCreditRequest } from 'store/cart/actions';
import { getAppliedStoreCreditFromTotalSegments } from 'store/cart/magentoSelectors';
import { siteCreditErrorSelector, storeCreditLoadingStateSelector, totalSegmentsSelector } from 'store/cart/selectors';
import type { Dispatch } from 'redux';

const ApplyStoreCreditWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  p {
    margin: 0;
  }

  button {
    padding: 0;
    height: 2rem;
    border: 1px solid black;
    color: black;
    width: 6rem;

    &:not([disabled]) {
      background-color: #f2f2f2;
    }

    &:hover:not([disabled]) {
      background-color: white;
    }

    &:disabled {
      border: none;
      background-color: lightgrey;
      color: white;
    }
  }
`;

const StyledCalloutBlock = styled(CalloutBlock)`
  height: auto;
  width: auto;
  padding: 0.5rem 1rem;
`;

const ErrorMessage = styled.div`
  font-family: 'Crimson Text', 'Georgia', serif;
  color: red;
  font-size: 14px;
  display: inline-block;
`;

export interface ApplyStoreCreditProps {
  balance: number;
  appliedStoreCredit: number;
  storeCreditLoadingState: LoadingState;
  applyStoreCredit: () => void;
  removeStoreCredit: () => void;
  err: string;
}

export const ApplyStoreCredit = ({
  balance,
  applyStoreCredit,
  removeStoreCredit,
  appliedStoreCredit,
  storeCreditLoadingState,
  err
}: ApplyStoreCreditProps): React.JSX.Element => {
  const [error, setError] = useState('');

  const isLoading = isFetching(storeCreditLoadingState);
  const remainingBal = (balance || 0) + appliedStoreCredit;

  useEffect(() => {
    setError(err);
  }, [err]);

  return (
    <Show when={appliedStoreCredit > 0 || balance > 0}>
      <StyledCalloutBlock>
        <ApplyStoreCreditWrapper>
          <StyledP secondary className="label">
            AU Credit ({numberToUSD(remainingBal)})
          </StyledP>

          <PrimaryLoadingButton
            onClick={() => {
              if (appliedStoreCredit) {
                removeStoreCredit();
              } else {
                applyStoreCredit();
              }
            }}
            isLoading={isLoading}
          >
            {appliedStoreCredit ? 'Remove' : 'Apply'}
          </PrimaryLoadingButton>
        </ApplyStoreCreditWrapper>
        <Show when={!!error}>
          <ErrorMessage>{error}</ErrorMessage>
        </Show>
      </StyledCalloutBlock>
    </Show>
  );
};

const mapStateToProps = (state: GlobalState) => ({
  balance: storeCreditSelector(state),
  appliedStoreCredit: getAppliedStoreCreditFromTotalSegments(totalSegmentsSelector(state)),
  storeCreditLoadingState: storeCreditLoadingStateSelector(state),
  err: siteCreditErrorSelector(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  applyStoreCredit: () => dispatch(applyStoreCreditRequest()),
  removeStoreCredit: () => dispatch(removeStoreCreditRequest())
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplyStoreCredit);
